/**
 * Hands-free Flux Agent conversation engine: one `AudioContext` for capture, voice-activity
 * detection and playback; PCM capture with pre-roll so the first syllable survives detection
 * delay; adaptive end-of-speech; barge-in while the agent is thinking or speaking; and gapless
 * sentence-by-sentence playback of a streamed reply.
 *
 * iOS Safari rules shape the design. The context is created and resumed synchronously inside
 * the click that starts voice mode (a context created later starts suspended and cannot be
 * resumed without another gesture), a silent buffer is played in the same gesture to unlock
 * output, and capture and playback reuse that one context. When the page is hidden the engine
 * pauses (iOS stops background microphones anyway) and waits for a tap to resume.
 *
 * Recordings are encoded as 16 kHz mono WAV in the browser rather than with `MediaRecorder`,
 * because the detector decides that speech started ~200 ms after it did and a compressed
 * recorder cannot be started in the past. Audio and text go only to the existing Flux Agent
 * routes; timings are reported locally and never sent anywhere.
 *
 * Capture runs in an `AudioWorklet` (`/prosody/flux-capture-worklet.js`) that feeds each
 * 2,048-sample frame to the Rust prosody analyzer (`crates/prosody`, WebAssembly) on the audio
 * thread and posts the frame with its RMS here. When an utterance ends the engine asks the
 * worklet for that span's prosody numbers (loudness, pauses, pitch; JSON from Rust) and sends
 * them with the message; the server keeps them only while conversation learning is on. Browsers
 * without `AudioWorklet`, or a build without the prosody package, fall back to a main-thread
 * `ScriptProcessorNode` and send no prosody.
 *
 * @module apps/web/src/lib/fluxAgentVoiceEngine
 */

import {
  createPlaybackQueue,
  createSentenceSplitter,
  createVoiceActivityDetector,
  downsample,
  encodeWav,
  speakWithBrowser,
  canSpeakWithBrowser,
  summarizeVoiceTurn,
  type PlaybackQueue,
  type ScheduledPlayback,
  type SentenceSplitter,
  type VoiceActivityDetector,
  type VoiceSensitivity,
  type VoiceTurnMarks,
  type VoiceTurnTimings,
} from '@/lib/fluxAgentVoice';

/** Visible engine state. */
export type VoiceEngineState =
  /** Not running; the microphone is closed. */
  | 'off'
  /** Opening the microphone after the start gesture. */
  | 'starting'
  /** Microphone open, waiting for the person to speak. */
  | 'listening'
  /** The person is speaking; the utterance is being captured. */
  | 'hearing'
  /** The utterance is being transcribed. */
  | 'transcribing'
  /** The reply is being generated; speaking now interrupts it. */
  | 'thinking'
  /** The reply is being spoken; speaking now interrupts it. */
  | 'speaking'
  /** The page was hidden; a tap resumes. */
  | 'paused';

/** Server and page operations the engine relies on. */
export interface VoiceEngineDeps {
  /** Transcribe a recording; resolves with trimmed text (possibly empty), throws with user copy. */
  transcribe(audio: Blob, mimeType: string): Promise<string>;
  /** Synthesize one sentence to encoded audio, or resolve `null` to use the browser voice. */
  synthesize(text: string, signal: AbortSignal): Promise<ArrayBuffer | null>;
  /**
   * Send the transcript as a chat turn, reporting streamed answer text through `onDelta`, with
   * the utterance's prosody JSON object when the worklet measured it. Resolves with the stored
   * reply text, or `null` when sending failed (the page shows why).
   */
  send(
    text: string,
    onDelta: (text: string, round: number) => void,
    prosody?: unknown
  ): Promise<string | null>;
}

/** Notifications from the engine to its UI. */
export interface VoiceEngineCallbacks {
  /** State changed. */
  onState(state: VoiceEngineState): void;
  /** Smoothed 0-1 microphone level, about 20 times per second while the microphone is open. */
  onLevel(level: number): void;
  /** A user-presentable problem, or `null` to clear it. */
  onError(message: string | null): void;
  /** Latency of a completed turn (reported when its first audio starts, or when it ends). */
  onTimings(timings: VoiceTurnTimings): void;
}

/** Engine construction options. */
export interface VoiceEngineOptions {
  /** Initial detector sensitivity. */
  sensitivity: VoiceSensitivity;
  /** Server transcription limit in bytes; utterances stop before their WAV would exceed it. */
  maxAudioBytes: number;
}

/** Sample rate of uploaded recordings: Whisper's native rate. */
const UPLOAD_RATE = 16_000;
/** Audio kept from before the detected onset, in milliseconds. */
const PRE_ROLL_MS = 500;
/** Threshold multiplier while the agent is thinking or speaking, against residual echo. */
const BARGE_IN_BOOST = 1.6;
/** Sustained energy required to interrupt the agent, in milliseconds. */
const BARGE_IN_ONSET_MS = 250;
/** Sustained energy required to start an utterance while listening, in milliseconds. */
const LISTEN_ONSET_MS = 200;
/** Longest utterance, in milliseconds. */
const MAX_UTTERANCE_MS = 45_000;
/** Where the capture worklet and the prosody WebAssembly package are served. */
const PROSODY_BASE = '/prosody/';
/** Longest wait for the worklet's prosody reply; transcription takes far longer, so this rarely binds. */
const PROSODY_WAIT_MS = 1_000;

/** The prosody module bytes, fetched once per page; `null` when the package is unavailable. */
let prosodyBytes: Promise<ArrayBuffer | null> | null = null;

/** Fetch the prosody WebAssembly bytes once; resolves `null` when the build does not include them. */
function loadProsodyBytes(): Promise<ArrayBuffer | null> {
  prosodyBytes ??= fetch(`${PROSODY_BASE}fluxability_prosody_bg.wasm`)
    .then(response => (response.ok ? response.arrayBuffer() : null))
    .catch(() => null);
  return prosodyBytes;
}

/** A queued sentence: decoded audio, or text for the browser synthesizer. */
type Playable =
  | AudioBuffer
  | {
      /** Sentence text spoken by the browser synthesizer when server speech is unavailable. */
      speech: string;
    };

/** Constructor for `AudioContext`, including Safari's prefixed one. */
function audioContextConstructor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/** True when this browser can run hands-free voice mode. */
export function canRunVoiceEngine(): boolean {
  return (
    !!audioContextConstructor() &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

/** Decode encoded audio, supporting Safari's callback-only `decodeAudioData`. */
function decode(context: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    const result = context.decodeAudioData(data, resolve, reject);
    if (result && typeof result.then === 'function') result.then(resolve, reject);
  });
}

/** The live conversation for one voice-mode session. Create it, then call `start()` in a click. */
export class VoiceEngine {
  /** Current state; read through `state`. */
  private current: VoiceEngineState = 'off';
  /** The single context used for analysis and playback; created in the start gesture. */
  private context: AudioContext | null = null;
  /** Open microphone stream. */
  private stream: MediaStream | null = null;
  /** Microphone source node. */
  private source: MediaStreamAudioSourceNode | null = null;
  /** Main-thread PCM tap, used only when the capture worklet is unavailable. */
  private processor: ScriptProcessorNode | null = null;
  /** Capture worklet running the Rust prosody analyzer on the audio thread. */
  private worklet: AudioWorkletNode | null = null;
  /** Contexts whose audio worklet module is already added. */
  private readonly workletContexts = new WeakSet<BaseAudioContext>();
  /** Sequence number of the latest worklet frame, -1 before the first. */
  private lastSeq = -1;
  /** First worklet frame of the utterance being captured, including pre-roll. */
  private utteranceStartSeq = -1;
  /** Pending prosody replies by request id. */
  private readonly prosodyWaiters = new Map<number, (json: unknown) => void>();
  /** Next prosody request id. */
  private prosodyRequest = 0;
  /** Silent sink keeping the processor running without echoing the microphone. */
  private mute: GainNode | null = null;
  /** Playback output. */
  private output: GainNode | null = null;
  /** Detector shared by listening and barge-in. */
  private readonly vad: VoiceActivityDetector;
  /** Sentence playback. */
  private readonly queue: PlaybackQueue<Playable>;
  /** Recent frames kept for pre-roll. */
  private preRoll: Float32Array[] = [];
  /** Frames of the utterance being captured; `null` when not capturing. */
  private captured: Float32Array[] | null = null;
  /** Samples in `captured`. */
  private capturedSamples = 0;
  /** Capture clock in milliseconds, advanced by frame durations. */
  private clockMs = 0;
  /** `performance.now()` of the latest frame, mapping the capture clock to wall time. */
  private frameAt = 0;
  /** Smoothed meter level. */
  private level = 0;
  /** Debug override for the measured RMS (see `injectLevel`). */
  private override: number | null = null;
  /** Incremented to cancel the spoken part of the current turn. */
  private turn = 0;
  /** Aborts sentence synthesis of the current turn. */
  private synthesis: AbortController | null = null;
  /** Serializes chat turns so a barge-in turn waits for the previous reply to be stored. */
  private sending: Promise<unknown> = Promise.resolve();
  /** Marks of the turn being timed. */
  private marks: VoiceTurnMarks = {};
  /** Whether the current marks were already reported. */
  private reported = false;
  /** Bound visibility handler, kept for removal. */
  private readonly onHidden = () => {
    if (document.visibilityState === 'hidden') this.pause();
  };

  /** Create an engine; nothing is opened until `start()`. */
  constructor(
    /** Microphone, audio-context, transcription, chat and speech services; injectable for tests. */
    private readonly deps: VoiceEngineDeps,
    /** UI notifications for phase, level, transcript, timing and error changes. */
    private readonly callbacks: VoiceEngineCallbacks,
    /** Sensitivity, server-speech availability and the upload size limit. */
    private readonly options: VoiceEngineOptions
  ) {
    this.vad = createVoiceActivityDetector({
      sensitivity: options.sensitivity,
      onsetMs: LISTEN_ONSET_MS,
      maxUtteranceMs: MAX_UTTERANCE_MS,
    });
    this.queue = createPlaybackQueue<Playable>(
      {
        now: () => this.context?.currentTime ?? 0,
        schedule: (item, at) => this.schedule(item, at),
      },
      {
        onStart: at => {
          const context = this.context;
          this.marks.firstAudioAt =
            performance.now() + (context ? Math.max(0, at - context.currentTime) * 1000 : 0);
          if (this.current === 'thinking') this.setState('speaking');
          this.report();
        },
      }
    );
  }

  /** Current state. */
  get state(): VoiceEngineState {
    return this.current;
  }

  /** True while reply audio is scheduled or playing. */
  get playing(): boolean {
    return this.queue.busy;
  }

  /** Change detector sensitivity immediately. */
  setSensitivity(sensitivity: VoiceSensitivity): void {
    this.vad.setSensitivity(sensitivity);
  }

  /**
   * Debug only: replace the measured microphone RMS with `level` until called with `null`.
   * Lets tests drive speech, silence and barge-in deterministically.
   */
  injectLevel(level: number | null): void {
    this.override = level;
  }

  /**
   * Start voice mode. Must run synchronously inside the user's click or tap: it creates and
   * resumes the shared `AudioContext` and plays a silent buffer to unlock output before the
   * first await. Resolves once the microphone is open.
   */
  async start(): Promise<void> {
    if (this.current !== 'off') return;
    const Context = audioContextConstructor();
    if (!Context) {
      this.callbacks.onError('This browser cannot run voice mode.');
      return;
    }
    this.callbacks.onError(null);
    this.setState('starting');
    try {
      this.context = new Context({ latencyHint: 'interactive' });
    } catch {
      this.context = new Context();
    }
    this.unlock(this.context);
    try {
      // Safari 16.4+: keep output on the speaker while recording.
      (navigator as unknown as { audioSession?: { type: string } }).audioSession!.type =
        'play-and-record';
    } catch {
      // Not supported; the default session applies.
    }
    document.addEventListener('visibilitychange', this.onHidden);
    window.addEventListener('pagehide', this.onHidden);
    await this.openMicrophone();
  }

  /** Resume after a pause. Call from a tap so the context may resume on iOS. */
  async resume(): Promise<void> {
    if (this.current !== 'paused' || !this.context) return;
    this.callbacks.onError(null);
    this.setState('starting');
    this.unlock(this.context);
    await this.openMicrophone();
  }

  /** End voice mode: stop capture and playback, close the microphone and the context. */
  stop(): void {
    this.cancelSpeech();
    this.closeMicrophone();
    document.removeEventListener('visibilitychange', this.onHidden);
    window.removeEventListener('pagehide', this.onHidden);
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.output = null;
    this.callbacks.onLevel(0);
    this.setState('off');
  }

  /** End the current utterance now instead of waiting for silence. */
  finishUtterance(): void {
    if (this.current !== 'hearing') return;
    this.vad.reset();
    void this.endUtterance(performance.now());
  }

  /** Stop the agent talking (tap to interrupt) and go back to listening. */
  interrupt(): void {
    if (this.current !== 'thinking' && this.current !== 'speaking') return;
    this.cancelSpeech();
    this.listen();
  }

  /** Pause on page hide: stop speech and capture, release the microphone, suspend output. */
  pause(): void {
    if (this.current === 'off' || this.current === 'paused') return;
    this.cancelSpeech();
    this.closeMicrophone();
    void this.context?.suspend().catch(() => undefined);
    this.callbacks.onLevel(0);
    this.setState('paused');
  }

  /** Update state and notify. */
  private setState(state: VoiceEngineState): void {
    if (this.current === state) return;
    this.current = state;
    this.callbacks.onState(state);
  }

  /** Resume the context and play one silent frame; must run inside a gesture on iOS. */
  private unlock(context: AudioContext): void {
    void context.resume().catch(() => undefined);
    try {
      const buffer = context.createBuffer(1, 1, context.sampleRate);
      const node = context.createBufferSource();
      node.buffer = buffer;
      node.connect(context.destination);
      node.start(0);
    } catch {
      // Output unlocks on the next gesture instead.
    }
    if (canSpeakWithBrowser()) {
      try {
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(''));
      } catch {
        // Browser voice fallback may stay locked; server audio is unaffected.
      }
    }
  }

  /** Open the microphone and connect the capture graph, then listen. */
  private async openMicrophone(): Promise<void> {
    const context = this.context;
    if (!context) return;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    } catch (error) {
      const name = (error as DOMException)?.name;
      this.callbacks.onError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Microphone access was blocked. Allow it in your browser’s site settings to talk to Flux Agent.'
          : name === 'NotFoundError'
            ? 'No microphone was found.'
            : 'The microphone could not be started.'
      );
      this.stop();
      return;
    }
    if (this.context !== context) {
      this.stream.getTracks().forEach(track => track.stop());
      return;
    }
    if (context.state === 'suspended') void context.resume().catch(() => undefined);
    this.output = this.output ?? context.createGain();
    this.output.connect(context.destination);
    this.source = context.createMediaStreamSource(this.stream);
    this.mute = context.createGain();
    this.mute.gain.value = 0;
    this.mute.connect(context.destination);
    const worklet = await this.openWorklet(context);
    if (this.context !== context || !this.source || !this.mute) {
      worklet?.disconnect();
      return;
    }
    if (worklet) {
      this.worklet = worklet;
      this.source.connect(worklet);
      worklet.connect(this.mute);
    } else {
      this.processor = context.createScriptProcessor(2048, 1, 1);
      this.processor.onaudioprocess = event => this.frame(event.inputBuffer.getChannelData(0));
      this.source.connect(this.processor);
      this.processor.connect(this.mute);
    }
    this.listen();
  }

  /** Start the capture worklet with the prosody analyzer, or resolve `null` to use the fallback. */
  private async openWorklet(context: AudioContext): Promise<AudioWorkletNode | null> {
    if (!context.audioWorklet || typeof AudioWorkletNode === 'undefined') return null;
    try {
      const bytes = await loadProsodyBytes();
      if (!bytes) return null;
      if (!this.workletContexts.has(context)) {
        await context.audioWorklet.addModule(`${PROSODY_BASE}flux-capture-worklet.js`);
        this.workletContexts.add(context);
      }
      const node = new AudioWorkletNode(context, 'flux-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { wasm: bytes.slice(0) },
      });
      node.port.onmessage = ({ data }) => {
        if (data?.type === 'frame') {
          this.lastSeq = data.seq;
          this.frame(data.samples, data.rms);
        } else if (data?.type === 'prosody') {
          const waiter = this.prosodyWaiters.get(data.id);
          this.prosodyWaiters.delete(data.id);
          let parsed: unknown;
          try {
            parsed = data.json?.byteLength ? JSON.parse(new TextDecoder().decode(data.json)) : null;
          } catch {
            parsed = null;
          }
          waiter?.(parsed);
        }
      };
      return node;
    } catch {
      return null;
    }
  }

  /** Ask the worklet for the prosody of frames `first..=last`; resolves `null` without a worklet or reply. */
  private requestProsody(first: number, last: number): Promise<unknown> {
    const worklet = this.worklet;
    if (!worklet || first < 0 || last < first) return Promise.resolve(null);
    const id = ++this.prosodyRequest;
    return new Promise(resolve => {
      const timer = setTimeout(() => {
        this.prosodyWaiters.delete(id);
        resolve(null);
      }, PROSODY_WAIT_MS);
      this.prosodyWaiters.set(id, json => {
        clearTimeout(timer);
        resolve(json);
      });
      worklet.port.postMessage({ type: 'summarize', id, first, last });
    });
  }

  /** Disconnect capture and stop the microphone tracks. */
  private closeMicrophone(): void {
    this.captured = null;
    this.capturedSamples = 0;
    this.preRoll = [];
    if (this.processor) this.processor.onaudioprocess = null;
    this.processor?.disconnect();
    if (this.worklet) this.worklet.port.onmessage = null;
    this.worklet?.disconnect();
    this.worklet = null;
    this.prosodyWaiters.forEach(resolve => resolve(null));
    this.prosodyWaiters.clear();
    this.lastSeq = -1;
    this.utteranceStartSeq = -1;
    this.source?.disconnect();
    this.mute?.disconnect();
    this.processor = null;
    this.source = null;
    this.mute = null;
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
    this.vad.reset();
  }

  /** Enter the listening state with listening thresholds. */
  private listen(): void {
    this.vad.reset();
    this.vad.configure(1, LISTEN_ONSET_MS);
    this.setState('listening');
  }

  /**
   * Process one capture frame: meter, pre-roll, capture, and detector transitions. `measured` is
   * the worklet's RMS; the fallback path measures it here.
   */
  private frame(input: Float32Array, measured?: number): void {
    const context = this.context;
    if (!context) return;
    const samples = measured === undefined ? new Float32Array(input) : input;
    const frameMs = (samples.length / context.sampleRate) * 1000;
    this.clockMs += frameMs;
    this.frameAt = performance.now();
    let rms = measured ?? 0;
    if (measured === undefined) {
      let sum = 0;
      for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
      rms = Math.sqrt(sum / Math.max(1, samples.length));
    }
    rms = this.override ?? rms;
    this.level = this.level * 0.6 + Math.min(1, rms * 8) * 0.4;
    this.callbacks.onLevel(this.level);

    this.preRoll.push(samples);
    while (this.preRoll.length * frameMs > PRE_ROLL_MS) this.preRoll.shift();
    if (this.captured) {
      this.captured.push(samples);
      this.capturedSamples += samples.length;
      const wavBytes = 44 + (this.capturedSamples / context.sampleRate) * UPLOAD_RATE * 2;
      if (this.options.maxAudioBytes > 0 && wavBytes > this.options.maxAudioBytes * 0.95) {
        this.vad.reset();
        void this.endUtterance(performance.now());
        return;
      }
    }

    const state = this.current;
    if (!['listening', 'hearing', 'thinking', 'speaking'].includes(state)) return;
    const event = this.vad.update(rms, this.clockMs);
    if (event === 'speech-start') {
      if (state === 'thinking' || state === 'speaking') this.cancelSpeech();
      this.vad.configure(1, LISTEN_ONSET_MS);
      this.captured = [...this.preRoll];
      this.utteranceStartSeq = this.lastSeq < 0 ? -1 : this.lastSeq - this.preRoll.length + 1;
      this.capturedSamples = this.captured.reduce((total, chunk) => total + chunk.length, 0);
      this.setState('hearing');
    } else if (event === 'speech-end' && state === 'hearing') {
      const lastVoiced = this.vad.lastVoicedAt;
      void this.endUtterance(
        lastVoiced === null ? this.frameAt : this.frameAt - (this.clockMs - lastVoiced)
      );
    } else if (event === 'speech-discard' && state === 'hearing') {
      this.captured = null;
      this.capturedSamples = 0;
      this.listen();
    }
  }

  /** Stop speaking the current turn: drop queued audio, abort synthesis, silence the browser voice. */
  private cancelSpeech(): void {
    this.turn++;
    this.queue.stop();
    this.synthesis?.abort();
    this.synthesis = null;
    if (canSpeakWithBrowser()) window.speechSynthesis.cancel();
  }

  /** Finish the captured utterance and transcribe it. `stoppedAt` is when speech last had energy. */
  private async endUtterance(stoppedAt: number): Promise<void> {
    const context = this.context;
    const frames = this.captured;
    this.captured = null;
    this.capturedSamples = 0;
    if (!context || !frames?.length) return this.listen();
    this.report();
    this.marks = { speechStoppedAt: stoppedAt, endpointAt: performance.now() };
    this.reported = false;
    this.setState('transcribing');
    const total = frames.reduce((sum, chunk) => sum + chunk.length, 0);
    const joined = new Float32Array(total);
    let offset = 0;
    for (const chunk of frames) {
      joined.set(chunk, offset);
      offset += chunk.length;
    }
    const wav = encodeWav(downsample(joined, context.sampleRate, UPLOAD_RATE), UPLOAD_RATE);
    // Measured on the audio thread while transcription runs; numbers only, never audio.
    const prosody = this.requestProsody(this.utteranceStartSeq, this.lastSeq);
    this.utteranceStartSeq = -1;
    let text = '';
    try {
      text = await this.deps.transcribe(
        new Blob([wav as BlobPart], { type: 'audio/wav' }),
        'audio/wav'
      );
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error.message : 'Transcription failed.');
    }
    this.marks.transcriptAt = performance.now();
    if (this.current !== 'transcribing') return;
    if (!text) return this.listen();
    this.callbacks.onError(null);
    void this.converse(text, await prosody);
  }

  /** Send one transcript (with its prosody, when measured) and speak the streamed reply sentence by sentence. */
  private async converse(text: string, prosody: unknown = null): Promise<void> {
    const turn = ++this.turn;
    const marks = this.marks;
    const synthesis = new AbortController();
    this.synthesis = synthesis;
    const splitter: SentenceSplitter = createSentenceSplitter();
    let round = -1;
    let spoke = false;
    this.vad.reset();
    this.vad.configure(BARGE_IN_BOOST, BARGE_IN_ONSET_MS);
    this.setState('thinking');

    const speak = (sentence: string) => {
      spoke = true;
      this.queue.enqueue(this.load(sentence, synthesis.signal));
    };
    const onDelta = (delta: string, deltaRound: number) => {
      marks.firstDeltaAt ??= performance.now();
      if (turn !== this.turn) return;
      if (deltaRound !== round) {
        if (round >= 0) splitter.reset();
        round = deltaRound;
      }
      splitter.push(delta).forEach(speak);
    };
    const previous = this.sending;
    const reply = (async () => {
      await previous.catch(() => undefined);
      return this.deps.send(text, onDelta, prosody ?? undefined);
    })();
    this.sending = reply;
    const result = await reply.catch(() => null);
    if (turn !== this.turn) return;
    if (result === null) {
      this.report();
      return this.listen();
    }
    splitter.flush().forEach(speak);
    if (!spoke) {
      // Nothing streamed (fallback route or a substituted reply): speak the stored text.
      const whole = createSentenceSplitter();
      [...whole.push(result), ...whole.flush()].forEach(speak);
    }
    await this.queue.drained();
    if (turn !== this.turn) return;
    this.report();
    this.synthesis = null;
    this.listen();
  }

  /** Synthesize and decode one sentence, falling back to the browser voice on any failure. */
  private async load(sentence: string, signal: AbortSignal): Promise<Playable | null> {
    const context = this.context;
    if (!context || signal.aborted) return null;
    try {
      const encoded = await this.deps.synthesize(sentence, signal);
      if (signal.aborted) return null;
      if (encoded) return await decode(context, encoded);
    } catch {
      if (signal.aborted) return null;
    }
    return canSpeakWithBrowser() ? { speech: sentence } : null;
  }

  /** Schedule one playable on the shared context (or the browser voice) at clock time `at`. */
  private schedule(item: Playable, at: number): ScheduledPlayback {
    const context = this.context;
    if (!(item instanceof AudioBuffer) || !context || !this.output) {
      const controller = new AbortController();
      const text = item instanceof AudioBuffer ? '' : item.speech;
      return {
        endsAt: null,
        handle: {
          stop: () => controller.abort(),
          ended: speakWithBrowser(text, controller.signal),
        },
      };
    }
    const node = context.createBufferSource();
    node.buffer = item;
    node.connect(this.output);
    let finish = () => {};
    const ended = new Promise<void>(resolve => (finish = resolve));
    node.onended = () => {
      node.disconnect();
      finish();
    };
    node.start(at);
    return {
      endsAt: at + item.duration,
      handle: {
        stop: () => {
          try {
            node.stop();
          } catch {
            // Already stopped.
          }
          finish();
        },
        ended,
      },
    };
  }

  /** Report the current turn's timings once, via the callback and `console.debug`. */
  private report(): void {
    if (this.reported || this.marks.endpointAt === undefined) return;
    this.reported = true;
    const timings = summarizeVoiceTurn(this.marks);
    console.debug('[flux-agent voice] turn latency (ms)', timings);
    this.callbacks.onTimings(timings);
  }
}
